import type { KeyboardEvent } from 'react';
import { AbstractViewModel } from '../../../utils/viewmodels/viewmodel';
import { useViewModel } from '../../../utils/viewmodels/useViewModel';
import type { ChatInputState } from './chat-input.state';

export class ChatInputViewModel extends AbstractViewModel<ChatInputState> {
  static initialState: ChatInputState = {
    text: '',
    isDisabled: false,
  };

  private readonly onSend: (text: string) => void;

  constructor(onSend: (text: string) => void, isDisabled: boolean) {
    super(ChatInputViewModel.initialState);
    this.setDisabled(isDisabled);
    this.onSend = onSend;

    this.handleSend = this.handleSend.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  setDisabled = (disabled: boolean): void => {
    this.update((state) => {
      state.isDisabled = disabled;
    });
  };

  setText = (value: string): void => {
    this.update((state) => {
      state.text = value;
    });
  };

  isSendDisabled = (): boolean => this.state.getValue().isDisabled || this.state.getValue().text.trim().length === 0;

  handleSend = (): void => {
    if (this.isSendDisabled()) return;

    this.onSend(this.state.getValue().text.trim());
    this.update((state) => {
      state.text = '';
    });
  };

  handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  };
}

export const useChatInputViewModel = (onSend: (text: string) => void, isDisabled: boolean) =>
  useViewModel<ChatInputState, ChatInputViewModel>(() => new ChatInputViewModel(onSend, isDisabled));
